import * as React from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";

/**
 * Flattens your existing menuItems (parents + sub items) into a
 * simple list of { text, route } pairs so voice commands can be
 * matched against the SAME data that drives the sidebar/navigate() calls.
 */
function flattenMenu(menuItems) {
  const flat = [];
  menuItems.forEach((item) => {
    if (item.route) {
      flat.push({ text: item.text, route: item.route });
    }
    if (item.items?.length) {
      item.items.forEach((sub) => {
        if (sub.route) {
          flat.push({ text: sub.text, route: sub.route });
        }
      });
    }
  });
  return flat;
}

// Small helper: turn "open flavour page" -> "flavour", strip filler words
const FILLER_WORDS = [
  "open", "go", "to", "show", "navigate", "the", "page", "section", "please",
];

function cleanTranscript(transcript) {
  return transcript
    .toLowerCase()
    .split(" ")
    .filter((w) => !FILLER_WORDS.includes(w))
    .join(" ")
    .trim();
}

/**
 * Very small fuzzy matcher: picks the menu entry whose text
 * has the most words in common with the cleaned transcript,
 * falling back to substring match.
 */
function findBestMatch(cleaned, flatMenu) {
  if (!cleaned) return null;

  // 1. exact substring match first (handles "flavour", "sub category", etc.)
  const substringMatch = flatMenu.find(
    (m) =>
      cleaned.includes(m.text.toLowerCase()) ||
      m.text.toLowerCase().includes(cleaned)
  );
  if (substringMatch) return substringMatch;

  // 2. word-overlap scoring fallback
  const cleanedWords = new Set(cleaned.split(" "));
  let best = null;
  let bestScore = 0;
  flatMenu.forEach((m) => {
    const menuWords = m.text.toLowerCase().split(" ");
    const score = menuWords.filter((w) => cleanedWords.has(w)).length;
    if (score > bestScore) {
      bestScore = score;
      best = m;
    }
  });
  return bestScore > 0 ? best : null;
}

/**
 * useVoiceNavigation
 *
 * IMPORTANT: the SpeechRecognition instance is created ONCE (on mount) and
 * kept alive for the lifetime of the component. All the "changeable" bits
 * (menuItems, navigate, callbacks) are read through refs inside the event
 * handlers instead of being effect dependencies — this is what previously
 * caused the hook to silently rebuild the recognizer on every parent
 * re-render (banner polling, redux updates, etc.), which is why voice
 * search worked "sometimes" and not other times.
 *
 * Usage inside SideNav:
 *   const { listening, toggleListening, lastHeard } = useVoiceNavigation(menuItems, navigate);
 *   <VoiceNavButton listening={listening} onToggle={toggleListening} />
 */
export function useVoiceNavigation(menuItems, navigate, options = {}) {
  const { onNoMatch, announceMatches = true } = options;

  const [listening, setListening] = React.useState(false);
  const [lastHeard, setLastHeard] = React.useState("");
  const recognitionRef = React.useRef(null);

  // --- refs for anything that can change between renders ---
  const flatMenuRef = React.useRef([]);
  const navigateRef = React.useRef(navigate);
  const onNoMatchRef = React.useRef(onNoMatch);
  const announceRef = React.useRef(announceMatches);

  // Keep refs fresh every render WITHOUT tearing down recognition
  React.useEffect(() => {
    flatMenuRef.current = flattenMenu(menuItems);
  }, [menuItems]);

  React.useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  React.useEffect(() => {
    onNoMatchRef.current = onNoMatch;
  }, [onNoMatch]);

  React.useEffect(() => {
    announceRef.current = announceMatches;
  }, [announceMatches]);

  const speak = React.useCallback((text) => {
    if (!announceRef.current || !window.speechSynthesis) return;
    try {
      const utter = new SpeechSynthesisUtterance(text);
      window.speechSynthesis.speak(utter);
    } catch (err) {
      console.error("speechSynthesis error:", err);
    }
  }, []);

  // Create the SpeechRecognition instance exactly ONCE.
  React.useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.warn("SpeechRecognition not supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setLastHeard(transcript);

      const cleaned = cleanTranscript(transcript);
      const match = findBestMatch(cleaned, flatMenuRef.current);

      if (match) {
        navigateRef.current(match.route);
        speak(`Opening ${match.text}`);
      } else {
        speak("Sorry, I didn't recognize that page.");
        onNoMatchRef.current?.(transcript);
      }
    };

    recognition.onerror = (e) => {
      // "no-speech" and "aborted" are normal/expected, don't spam console as errors
      if (e.error === "no-speech" || e.error === "aborted") {
        console.log("Voice recognition:", e.error);
      } else {
        console.error("Voice recognition error:", e.error);
      }
      setListening(false);
    };

    recognition.onend = () => {
      setListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try {
        recognition.stop();
      } catch (err) {
        // ignore — already stopped
      }
      recognitionRef.current = null;
    };
    // Intentionally empty: this must run ONCE on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleListening = React.useCallback(() => {
    const recognition = recognitionRef.current;
    if (!recognition) {
      console.warn("Speech recognition not available.");
      return;
    }

    if (listening) {
      try {
        recognition.stop();
      } catch (err) {
        console.error("Error stopping recognition:", err);
      }
      setListening(false);
    } else {
      try {
        recognition.start();
        setListening(true);
      } catch (err) {
        // Most common cause: "already started" — recover gracefully
        console.error("Error starting recognition:", err);
        try {
          recognition.stop();
        } catch (_) {}
        setListening(false);
      }
    }
  }, [listening]);

  return { listening, toggleListening, lastHeard };
}

/**
 * Drop-in mic button for the Toolbar next to ChatNotification/Notification/AccountMenu.
 * Styled to match the red AppBar (white icon, green when actively listening).
 */
export function VoiceNavButton({ listening, onToggle }) {
  return (
    <Tooltip title={listening ? "Listening... (click to stop)" : "Voice navigation"}>
      <IconButton onClick={onToggle} size="small" sx={{ ml: 1, color: "white" }}>
        {listening ? (
          <MicIcon sx={{ color: "#7CFC00" }} />
        ) : (
          <MicOffIcon sx={{ color: "white" }} />
        )}
      </IconButton>
    </Tooltip>
  );
}